import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    ShoppingBag,
    HeartPulse,
    Banknote,
    GraduationCap,
    Landmark,
    Cpu,
    BookOpen,
    Scale,
    Plane,
} from "lucide-react";
import { Card } from "../components/ui/card";

const INDUSTRIES = [
    {
        icon: ShoppingBag,
        name: "E-commerce",
        desc: "Shopify, Magento, WooCommerce, and custom platforms. The highest ADA lawsuit risk in the US — with rapidly increasing demand letters and class actions.",
        tags: ["WCAG 2.2", "ADA", "EAA"],
        risk: "high",
    },
    {
        icon: HeartPulse,
        name: "Healthcare",
        desc: "HIPAA + accessibility intersection, patient portals, telemedicine platforms, and accessible health forms (PDFs).",
        tags: ["WCAG 2.2", "ADA", "Section 508", "HHS"],
    },
    {
        icon: Banknote,
        name: "Banking & Finance",
        desc: "Regulatory compliance, online banking, mobile apps, statements and disclosures — including PDF accessibility for legally-required documents.",
        tags: ["WCAG 2.2", "ADA", "EAA", "PDF/UA"],
    },
    {
        icon: GraduationCap,
        name: "Education",
        desc: "EdTech, LMS platforms, Section 504/IDEA compliance, and EPUB/PDF accessible textbooks for inclusive learning environments.",
        tags: ["WCAG 2.2", "Section 508", "EPUB 3"],
    },
    {
        icon: Landmark,
        name: "Government",
        desc: "Section 508, BITV 2.0, RGAA, AODA, GIGW (India). Accessible forms, public documents, and citizen-facing services across jurisdictions.",
        tags: ["Section 508", "BITV", "RGAA", "AODA"],
    },
    {
        icon: Cpu,
        name: "SaaS",
        desc: "VPAT requirements for enterprise sales, B2B procurement gates, and accessibility as a competitive differentiator in RFPs.",
        tags: ["WCAG 2.2", "VPAT 2.5", "EN 301 549"],
    },
    {
        icon: BookOpen,
        name: "Publishing & Media",
        desc: "EPUB 3 accessibility, accessible digital publications, and born-accessible content workflows for publishers and media houses.",
        tags: ["EPUB 3", "WCAG 2.2"],
    },
    {
        icon: Scale,
        name: "Legal & Insurance",
        desc: "Accessible contracts, policy documents, and consent forms — PDF/UA compliance for client-facing legal documentation.",
        tags: ["PDF/UA", "WCAG 2.2", "ADA"],
    },
    {
        icon: Plane,
        name: "Travel & Hospitality",
        desc: "Booking platforms, mobile apps, and accessibility compliance for global markets including ADA, EAA, and AODA jurisdictions.",
        tags: ["WCAG 2.2", "ADA", "EAA"],
    },
];

const Hero = () => (
    <section className="relative overflow-hidden border-b border-brand-border" aria-labelledby="industries-hero">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-white via-white to-[#F1F7F8]" aria-hidden="true" />
        <div className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-[#127A75]/10 blur-3xl -z-10" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
            <p className="text-sm font-semibold uppercase tracking-wider text-brand-teal">Industries</p>
            <h1 id="industries-hero" className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-text tracking-tight max-w-4xl">
                Industry-Specific Accessibility Expertise
            </h1>
            <p className="mt-5 text-lg text-brand-body max-w-3xl">
                Different industries face different compliance risks. We specialize in the regulations and patterns unique to yours.
            </p>
        </div>
    </section>
);

const IndustryCard = ({ ind }) => {
    const Icon = ind.icon;
    return (
        <Card
            className="h-full p-7 border-brand-border hover:shadow-cardHover transition-shadow duration-200 bg-white group"
            data-testid={`industry-card-${ind.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="w-12 h-12 rounded-md bg-brand-soft text-brand-teal inline-flex items-center justify-center group-hover:bg-brand-teal group-hover:text-white transition-colors">
                    <Icon size={24} aria-hidden="true" />
                </div>
                {ind.risk === "high" && (
                    <span className="inline-flex items-center rounded-full bg-[#FEE2E2] text-[#991B1B] border border-[#FECACA] px-2.5 py-0.5 text-xs font-semibold">
                        High Lawsuit Risk
                    </span>
                )}
            </div>
            <h2 className="mt-5 text-xl font-bold text-brand-text">{ind.name}</h2>
            <p className="mt-3 text-sm text-brand-body leading-relaxed">{ind.desc}</p>
            <div className="mt-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-brand-muted">Common standards</p>
                <ul className="mt-2 flex flex-wrap gap-2">
                    {ind.tags.map((t) => (
                        <li key={t}>
                            <span className="inline-flex items-center rounded-md bg-brand-soft border border-brand-border px-2.5 py-1 text-xs font-semibold text-brand-text">
                                {t}
                            </span>
                        </li>
                    ))}
                </ul>
            </div>
            <Link
                to="/contact"
                aria-label={`Talk to us about accessibility for ${ind.name}`}
                className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-brand-teal hover:text-brand-tealBright"
            >
                Learn more
                <ArrowRight size={14} aria-hidden="true" />
            </Link>
        </Card>
    );
};

const Grid = () => (
    <section aria-labelledby="industries-grid" className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 id="industries-grid" className="sr-only">All industries</h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {INDUSTRIES.map((ind) => (
                    <li key={ind.name}>
                        <IndustryCard ind={ind} />
                    </li>
                ))}
            </ul>
        </div>
    </section>
);

const CTA = () => (
    <section aria-labelledby="industries-cta" className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#0A2540] via-[#0F3552] to-[#127A75]" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
            <div className="max-w-3xl">
                <h2 id="industries-cta" className="text-3xl sm:text-4xl font-bold text-white">
                    Don't see your industry?
                </h2>
                <p className="mt-4 text-lg text-white/85">
                    We've audited platforms across dozens of industries. Tell us about yours and we'll outline a tailored compliance plan.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                    <Link
                        to="/contact"
                        className="inline-flex items-center gap-2 rounded-md bg-white text-brand-navy hover:bg-[#E6FFFB] font-semibold px-6 py-3 transition-colors touch-target"
                        data-testid="industries-cta-contact"
                    >
                        Talk to us
                        <ArrowRight size={18} aria-hidden="true" />
                    </Link>
                    <Link
                        to="/free-audit"
                        className="inline-flex items-center justify-center rounded-md border-2 border-white/30 text-white hover:bg-white/10 font-semibold px-6 py-3 transition-colors touch-target"
                        data-testid="industries-cta-audit"
                    >
                        Get a Free Mini Audit
                    </Link>
                </div>
            </div>
        </div>
    </section>
);

export default function Industries() {
    useEffect(() => {
        document.title = "Industries — AccessNova";
    }, []);

    return (
        <>
            <Hero />
            <Grid />
            <CTA />
        </>
    );
}
